import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Check, X, Loader2, Heart } from 'lucide-react';
import axios from 'axios';
import { getRsvpButtonShadowStyle } from '../../lib/utils/buttonShadow';
import { resolveRsvpLabels } from '../../lib/utils/rsvpLabels';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * RSVPForm — works in two modes:
 * 1. Self-contained (V2): pass `slug` prop, form handles its own API call
 * 2. Callback (legacy): pass `onSubmit`, `isSubmitting`, `submitSuccess` props
 */
export default function RSVPForm({ 
  onSubmit,
  slug,
  accentColor,
  colorTheme = {},
  fontStyle = 'serif',
  labels = {},
  isSubmitting: externalSubmitting = false,
  submitSuccess: externalSuccess = false,
  buttonShadowStrength = 0,
  // Phase A — surface parity props (all optional, additive)
  customStyle = {},
  disableSubmit = false,
}) {
  // Phase B Lite — apply canonical resolver so all upstream label shapes
  // (canonical + legacy aliases) read uniformly.
  const L = resolveRsvpLabels(labels);
  const [form, setForm] = useState({
    name: '',
    attending: null,
    guest_count: 1,
    dietary: '',
    message: ''
  });
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const [internalSuccess, setInternalSuccess] = useState(false);
  const [error, setError] = useState('');

  const isSubmitting = slug ? internalSubmitting : externalSubmitting;
  const submitSuccess = slug ? internalSuccess : externalSuccess;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.attending === null || !form.name.trim()) return;
    // Phase A — review-mode guard: keep the UI visible but skip the network call.
    if (disableSubmit) {
      setError('Review mode — RSVP submission is disabled on the preview link.');
      return;
    }
    setError('');

    if (slug) {
      // Self-contained mode: handle API call internally
      setInternalSubmitting(true);
      try {
        await axios.post(`${API}/api/public/invite/${slug}/rsvp`, form);
        setInternalSuccess(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      } finally {
        setInternalSubmitting(false);
      }
    } else if (onSubmit) {
      onSubmit(form);
    }
  };

  const fontClass = {
    serif: 'font-serif',
    sans: 'font-sans',
    script: 'font-serif italic'
  }[fontStyle] || 'font-serif';

  // Phase A — customStyle takes precedence over legacy colorTheme/accentColor props.
  const resolvedAccent = customStyle.accentColor || accentColor || colorTheme.accent || '#d4af37';
  const primaryColor = customStyle.textColor || colorTheme.primary || '#1a1a1a';
  const bgColor = customStyle.bgColor || colorTheme.background || '#f5f5f0';
  const submitBg = customStyle.buttonColor || resolvedAccent;
  const submitText = customStyle.buttonTextColor || '#fff';
  const headingFontFamily = customStyle.fontFamily || undefined;

  if (submitSuccess) {
    return (
      <div className={`text-center py-12 ${fontClass}`}>
        <div 
          className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${resolvedAccent}20` }}
        >
          <Heart className="w-10 h-10" style={{ color: resolvedAccent }} />
        </div>
        <h3 className="text-2xl font-semibold mb-2" style={{ color: primaryColor, fontFamily: headingFontFamily }}>
          {L.thankYouTitle || 'Thank You!'}
        </h3>
        <p className="text-gray-600">
          {form.attending 
            ? (L.thankYouAccept || "We're so excited to celebrate with you!")
            : (L.thankYouDecline || "We'll miss you, but thank you for letting us know.")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 ${fontClass}`}>
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold mb-2" style={{ color: primaryColor, fontFamily: headingFontFamily }}>
          {L.heading || 'Will you be joining us?'}
        </h3>
        <p className="text-gray-500 text-sm">{L.subheading || 'Please let us know by filling out the form below'}</p>
      </div>

      {/* Attending Yes/No */}
      <div className="flex justify-center gap-4 mb-6">
        <button
          type="button"
          onClick={() => setForm(prev => ({ ...prev, attending: true }))}
          className="flex items-center gap-2 px-8 py-4 rounded-lg border-2 transition-all"
          style={form.attending === true ? { 
            borderColor: resolvedAccent, 
            backgroundColor: `${resolvedAccent}15`,
            color: primaryColor 
          } : {
            borderColor: '#d1d5db',
            color: primaryColor
          }}
        >
          <Check className="w-5 h-5" />
          <span className="font-medium">{L.yesLabel || 'Joyfully Accept'}</span>
        </button>
        <button
          type="button"
          onClick={() => setForm(prev => ({ ...prev, attending: false }))}
          className="flex items-center gap-2 px-8 py-4 rounded-lg border-2 transition-all"
          style={form.attending === false ? { 
            borderColor: '#999', 
            backgroundColor: '#f5f5f5',
            color: '#666' 
          } : {
            borderColor: '#d1d5db',
            color: primaryColor
          }}
        >
          <X className="w-5 h-5" />
          <span className="font-medium">{L.noLabel || 'Regretfully Decline'}</span>
        </button>
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label className="text-sm font-medium" style={{ color: primaryColor }}>
          {L.nameLabel || 'Your Name'} *
        </Label>
        <Input
          value={form.name}
          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter your full name"
          required
          className="border-gray-200 focus:border-gray-400"
          style={{ backgroundColor: 'transparent', color: primaryColor }}
        />
      </div>

      {/* Guest Count - only if attending */}
      {form.attending && (
        <div className="space-y-2">
          <Label className="text-sm font-medium" style={{ color: primaryColor }}>
            {L.guestCountLabel || 'Number of Guests (including yourself)'}
          </Label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, guest_count: Math.max(1, prev.guest_count - 1) }))}
              className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50"
              style={{ color: primaryColor }}
            >
              -
            </button>
            <span className="text-xl font-semibold w-8 text-center" style={{ color: primaryColor }}>
              {form.guest_count}
            </span>
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, guest_count: Math.min(10, prev.guest_count + 1) }))}
              className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50"
              style={{ color: primaryColor }}
            >
              +
            </button>
          </div>
        </div>
      )}

      {/* Dietary Restrictions - only if attending */}
      {form.attending && (
        <div className="space-y-2">
          <Label className="text-sm font-medium" style={{ color: primaryColor }}>
            {L.dietaryLabel || 'Dietary Restrictions (optional)'}
          </Label>
          <Input
            value={form.dietary}
            onChange={(e) => setForm(prev => ({ ...prev, dietary: e.target.value }))}
            placeholder="e.g., Vegetarian, Gluten-free, Allergies..."
            className="border-gray-200 focus:border-gray-400"
            style={{ backgroundColor: 'transparent' }}
          />
        </div>
      )}

      {/* Message */}
      <div className="space-y-2">
        <Label className="text-sm font-medium" style={{ color: primaryColor }}>
          {L.messageLabel || 'Message for the Couple (optional)'}
        </Label>
        <Textarea
          value={form.message}
          onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
          placeholder="Share your wishes or a note..."
          rows={3}
          className="border-gray-200 focus:border-gray-400 resize-none"
          style={{ backgroundColor: 'transparent' }}
        />
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-500 text-center mb-3" role="alert" data-testid="rsvp-error">
          {error}
        </p>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={form.attending === null || !form.name.trim() || isSubmitting}
        className="w-full py-6 text-lg font-medium transition-all rsvp-submit-shadow"
        data-testid="rsvp-submit-btn"
        style={{ 
          backgroundColor: submitBg, 
          color: submitText,
          opacity: (form.attending === null || !form.name.trim()) ? 0.5 : 1,
          ...getRsvpButtonShadowStyle(buttonShadowStrength),
        }}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          L.submitLabel || 'Send RSVP'
        )}
      </Button>
    </form>
  );
}
